import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateProgram, useTrainingCategories } from "../hooks";
import type { TrainingProgramCreateRequest } from "../types";

const EMPTY: TrainingProgramCreateRequest = {
  title: "",
  categoryId: null,
  description: null,
  providerName: null,
  venue: null,
  startDate: null,
  endDate: null,
  estimatedCost: null,
};

function extractMsg(err: unknown) {
  const e = err as any;
  return e?.response?.data?.message ?? e?.message ?? "Something went wrong.";
}

export default function TrainingProgramCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<TrainingProgramCreateRequest>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateProgram();
  const categoriesQuery = useTrainingCategories();
  const categories = categoriesQuery.data ?? [];

  function set<K extends keyof TrainingProgramCreateRequest>(k: K, v: TrainingProgramCreateRequest[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function handleSave() {
    setError(null);
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (!form.categoryId) { setError("Category is required."); return; }
    try {
      const prog = await create.mutateAsync(form);
      navigate(`/hr/training/programs/${prog.id}`);
    } catch (err) { setError(extractMsg(err)); }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800">← Back</button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">New Training Program</h1>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-4">
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Title <span className="text-red-500">*</span></span>
          <input className="input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Annual Data Protection Awareness" disabled={create.isPending} />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Category <span className="text-red-500">*</span></span>
          <select className="input" value={form.categoryId ?? ""} onChange={(e) => set("categoryId", e.target.value ? Number(e.target.value) : null)} disabled={create.isPending}>
            <option value="">{categories.length === 0 ? "Loading…" : "— Select category —"}</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Provider</span>
            <input className="input" value={form.providerName ?? ""} onChange={(e) => set("providerName", e.target.value || null)} placeholder="e.g. HR Department" disabled={create.isPending} />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Venue</span>
            <input className="input" value={form.venue ?? ""} onChange={(e) => set("venue", e.target.value || null)} placeholder="e.g. Boardroom / Online" disabled={create.isPending} />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">Start Date</span>
            <input type="date" className="input" value={form.startDate ?? ""} onChange={(e) => set("startDate", e.target.value || null)} disabled={create.isPending} />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">End Date</span>
            <input type="date" className="input" value={form.endDate ?? ""} min={form.startDate ?? undefined} onChange={(e) => set("endDate", e.target.value || null)} disabled={create.isPending} />
          </label>
          <label className="block space-y-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-slate-700">Estimated Cost (KES)</span>
            <input type="number" min={0} step="0.01" className="input" value={form.estimatedCost ?? ""} onChange={(e) => set("estimatedCost", e.target.value ? Number(e.target.value) : null)} placeholder="0.00" disabled={create.isPending} />
          </label>
        </div>

        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Description</span>
          <textarea className="input min-h-24 resize-y" value={form.description ?? ""} onChange={(e) => set("description", e.target.value || null)} placeholder="Describe the program objectives…" disabled={create.isPending} />
        </label>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={handleSave} disabled={create.isPending} className="rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
          {create.isPending ? "Creating…" : "Create Program"}
        </button>
      </div>
    </div>
  );
}
